import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { Link, Route, useParams } from "react-router-dom";
import ServiceConfiguratorBlocksList from "./ServiceConfiguratorBlocksList";
import ServiceConfiguratorOpenBlock from "./ServiceConfiguratorOpenBlock";
import useCountryAxiosInstance from "../hooks/useCountryAxiosInstance";
import apiRequest from "../services/apiRequest";
import { PlusCircleIcon, UserPlusIcon, UserIcon, CheckCircleIcon} from "@heroicons/react/24/solid";
import { PageHeading } from "../ui-components/Container";
import ErrorPage from "../ui-components/pages/ErrorPage";
import Button from "../ui-components/Button";
import defaultConfigurationRow from "./defaultConfigurationRow";
import Spinner from "../ui-components/Spinner";
import ServiceConfiguratorDeleteBlock from "./ServiceConfiguratorDeleteBlock";
import ServiceConfiguratorCreateTranslation from "./ServiceConfiguratorCreateTranslation";
import getServiceLocale from "../api/getServiceLocale";
import CountryContext from "../CountryContext";
import ServiceConfiguratorPreview from "./ServiceConfiguratorPreview";
import ListPlaceholder from "./ListPlaceholder";
import useGetServiceConfiguration from "../hooks/useGetServiceConfiguration";
import ModalPortal from "./ModalPortal";
import _ from "lodash";
import {NotificationsContext} from "../ui-components/Notifications";
import {FormattedMessage, useIntl} from "react-intl";
import ServiceChecklistListing from "./ServiceChecklistListing";
import useDispatchServiceChecklist from "../hooks/useDispatchServiceChecklist";
import ServiceApListing from "./ServiceApListing";
import useDispatchServiceAp from "../hooks/useDispatchServiceAp";
import {ArrowPathIcon, LinkIcon} from "@heroicons/react/24/outline";
import Badge from "@ui-components/Badge";
import {AssociateServicesModal} from "../feature/services";
import Alert from "../ui-components/Alert";
import {ServiceConfiguratorVersionManager} from "./ServiceConfiguratorVersionManager";
import {
  useCrossServicesConfigurations
} from "../feature/cross_services_configurations/hooks/useCrossServicesConfigurations";
import Tabs from "../ui-components/Tabs";
import {useServices} from "@feature/services";

import { TCSServiceModal } from "./Modals/TCSServiceModal";

const SECTIONS = ['steps', 'checklists', 'ap']

const ServiceConfigurator = () => {
  const intl = useIntl();
  const {push} = useContext(NotificationsContext);
  const {country} = useContext(CountryContext);
  const {serviceId} = useParams();
  const [data, setData] = useState(null);
  const [crossData, setCrossData] = useState(null);
  const [roleTypes, setRoleTypes] = useState(null);
  const [service, setService] = useState(null);
  const {services, reFetchOnlyOne} = useServices();
  const [TCSMetadata_, setTCSMetadata_] = useState(null);
  const [associatedQuotationService, setAssociatedQuotationService] = useState(undefined)
  const [associatedDesignServices, setAssociatedDesignServices] = useState(undefined);
  const [error, setError] = useState(null);
  const countryApiRequest = useCountryAxiosInstance();
  const [openBlock, setOpenBlock] = useState(false);
  const [openTCSBlock, setOpenTCSBlock] = useState(false);
  const [isTCSLoading, setIsTCSLoading] = useState(false);
  const [openBlockServiceBlock, setOpenBlockServiceBlock] = useState(null);
  const [openSecondBlock, setOpenSecondBlock] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteBlock, setDeleteBlock] = useState(null);
  const [createTranslation, setCreateTranslation] = useState(null);
  const [serviceLocale, setServiceLocale] = useState(null);
  const getServiceConfiguration = useGetServiceConfiguration(true);
  const {getCrossServicesConfigurations} = useCrossServicesConfigurations();
  const [blocksSelected, setBlocksSelected] = useState([]);
  const [selectedSection, setSelectedSection] = useState(SECTIONS[0]);
  const [serviceAssociationModalOpen, setServiceAssociationModalOpen] = useState(false);
  const dispatchChecklist = useDispatchServiceChecklist(serviceId);
  const dispatchAp = useDispatchServiceAp(serviceId);
  const [versions, setVersions] = useState([]);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [newVersion, setNewVersion] = useState(null);
  const versionsWithNew = [...versions, ...(newVersion !== null ? [newVersion] : [])];
  const showAlert = newVersion !== null;

  const fetchServiceConfiguration = useCallback(() => {
    return getServiceConfiguration(serviceId, newVersion || currentVersion);
  }, [serviceId, getServiceConfiguration]);

  const fetchCrossServiceConfiguration = useCallback(() => {
    return getCrossServicesConfigurations(serviceId, newVersion || currentVersion);
  }, [serviceId, getCrossServicesConfigurations]);

  const fetchServiceVersions = async () => {
    const result = await countryApiRequest.get(`/services/${service.id}/versions`);
    setVersions(result);
  }

  const setNewOrder = (block, mapBlock, newPosition, oldPosition) => {
    if (mapBlock.id === block.id) {
      return {...mapBlock, order: newPosition};
    }
    if (mapBlock.order === newPosition) {
      return {...mapBlock, order: oldPosition};
    }
    return mapBlock;
  };

  const fetchConfiguration = async (cancel) => {
    try {
      const [types, locale] = await Promise.all([
        // fetchServiceConfiguration(),
        apiRequest.get(`/roles`),
        // countryApiRequest.get(`/services/${serviceId}`),
        getServiceLocale(country, serviceId),
      ]);

      if (!cancel) {
        setServiceLocale(locale.data);
        // setService(dbService);
        // setServices([dbService]);
        setRoleTypes(types.filter((t) => t.name !== "admin"));
        // setData(dbConfiguration);
      }
    } catch (error) {
      if (!cancel) {
        setError(true);
      }
    }
  };
  const approveDraftVersion = async () => {
    try {
      setSubmitting(true);
      await countryApiRequest.put(`/services/${service.id}`,
        {
          ...service,
          active_version: currentVersion,
        });
      let cancel = false;
      await fetchConfiguration(cancel)
      await fetchServiceVersions();
      await reFetchOnlyOne(service.id);
      return () => {
        cancel = true;
      }
    } catch (error) {
      console.log(error);
    }
  }

  const deleteDraftVersion = async () => {
    try {
      setSubmitting(true);
      await countryApiRequest.delete(`/services_configurations/service/${service.id}/version/${currentVersion}`);
      let cancel = false;
      await fetchConfiguration(cancel)
      await fetchServiceVersions();
      return () => {
        cancel = true;
      }
    } catch (error) {
      console.log(error);
    }
  }

  const createElement = useCallback(
    (data) => {
      return countryApiRequest.post(
        `/services_configurations`,
        data
      );
    },
    [countryApiRequest]
  );

  const deleteElement = useCallback(
    async (id) => {
      const block = data.find((b) => b.id === id);
      if (currentVersion > block.version) {
        createElement({
            ...defaultConfigurationRow,
            ...block,
            serviceId: parseInt(serviceId, 10),
            flg_deleted: true,
            version: newVersion || currentVersion,
          }
        )
      } else {
        await countryApiRequest.put(
          `/services_configurations/${id}/version/${currentVersion}`,
          {
            ...block,
            flg_deleted: true,
          }
        )
      }
    },
    [countryApiRequest, data]
  );

  const copySelectedElement = useCallback(
    async (newBlock, blockToCopy) => {
      // Bisogna copiare anche i nodi con type='option' e input_id = blockToCopy.id
      // Fai chiamate multiple
      // 1 - get di tutti i nodi con type='option' e input_id = blockToCopy.id
      const newBlockResult = await countryApiRequest.post(`/services_configurations`, {
          ...newBlock,
        })

      const newBlockId = newBlockResult.id;
      // remove id from options
      const optionsResult = data.filter(
        (b) => b.input_id === blockToCopy.id && b.type === "option"
      ).map((option) => {
        const { id, ...rest } = option;
        return rest;
      });

      if (optionsResult.length > 0) {
        // 3 - crea i sotto nodi (POST) modificando il campo input_id con l'id del nuovo nodo principale
        return countryApiRequest.post(
          `/services_configurations/bulk`,
          optionsResult.map((option) => ({
            ...option,
            input_id: newBlockId,
            version: currentVersion,
          }))
        )
      }
      return null;
    },
    [countryApiRequest, currentVersion, data]
  );

  const moveBlock = useCallback(
    async (block, newPosition, oldPosition) => {
      try {
        setSubmitting(true);
        let list = [];
        if (block.type === "step" || block.type === "service") {
          list = data.filter((b) =>b.service_id === service.id && ( b.type === "step" || b.type === "service"));
        } else if (block.recursive_question_id) {
          list = data.filter(
            (b) => b.recursive_question_id === block.recursive_question_id
          );
        } else if (block.type === "option") {
          list = data.filter(
            (b) => b.input_id === block.input_id && b.type === "option"
          );
        } else if (block.step_id) {
          list = data.filter((b) => b.step_id === block.step_id);
        }

        if (!list.length) {
          return;
        }
        let payload = list.map((b) => setNewOrder(block, b, newPosition, oldPosition))
        payload = _.sortBy(payload, ['order']).map((el, i) => ({...el, order: i}))
        /*
        we need to check if we are editing a version
         */
        await countryApiRequest.put(
          `/services_configurations/bulk/version/${currentVersion}`,
          payload
        );
        const newData = await fetchServiceConfiguration();
        setData(newData);
      } catch (error) {
        console.log(error);
      } finally {
        setSubmitting(false);
      }
    },
    [data, setSubmitting, countryApiRequest, fetchServiceConfiguration]
  );

  const selectBlock = useCallback(
    (block) => {
      setBlocksSelected([...blocksSelected, block]);
    },
    [blocksSelected]
  );

  const unselectBlock = useCallback(
    (block) => {
      setBlocksSelected(blocksSelected.filter((b) => b.id !== block.id));
    },
    [blocksSelected]
  );

  let labelData;  // Is undefined if service is not loaded yet
  switch (service?.class) {
    case 0:  // Progettazione
      labelData = {type: "info", labelId: "design_service", labelDefaultMessage: "Progettazione"};
      break;
    case 1:  // Quotazione
      labelData = {type: "success", labelId: "quotation_service", labelDefaultMessage: "Quotazione"};
      break;
    case 2:  // Servizio
      labelData = {type: "neutralAlt", labelId: "service_service", labelDefaultMessage: "Servizio"};
      break;
  }

  /**
   * Copia gli elementi selezionati e li incolla nello step selezionato
   * @param {Object} block Blocco in cui incollare gli elementi selezionati
   * @returns {undefined}
   */
  const pasteBlocks = useCallback(
    async (block) => {
      if (!blocksSelected.length) {
        return null;
      }
      try {
        setSubmitting(true);
        let order = 0;
        let stepOrRecursiveId = {};
        if (block.type === "step") {
          order = data.filter((b) => b.step_id === block.id).length;
          stepOrRecursiveId = { step_id: block.id };
        } else {
          order = data.filter(
            (b) => b.recursive_question_id === block.id
          ).length;
          stepOrRecursiveId = { recursive_question_id: block.id };
        }

        await Promise.all(
          blocksSelected.map((blockToCopy, index) => {
            const { id, created_at, step_id, recursive_question_id, ...data } =
              blockToCopy;
            // Nel caso in cui il nodo da copiare sia di tipo question e abbia input_type = select o multi_select
            // bisogna copiare anche i nodi con type='option' e input_id = blockToCopy.id
            if (
              blockToCopy.type === "question" &&
              (blockToCopy.input_type === "select" ||
                blockToCopy.input_type === "multi_select")
            ) {
              const newSelectBlock = {
                ...defaultConfigurationRow,
                ...data,
                ...stepOrRecursiveId,
                order: order + index,
                version: currentVersion,
              };
              return copySelectedElement(newSelectBlock, blockToCopy);
            } else {
              return createElement({
                ...defaultConfigurationRow,
                ...data,
                ...stepOrRecursiveId,
                order: order + index,
                version: currentVersion,
              });
            }
          })
        );

        const newData = await fetchServiceConfiguration();
        setData(newData);
        setBlocksSelected([]);
      } catch (error) {
        console.log(error);
      } finally {
        setSubmitting(false);
      }
    },
    [
      blocksSelected,
      data,
      createElement,
      fetchServiceConfiguration,
      setBlocksSelected,
      setData,
    ]
  );

  const deleteAndFetch = useCallback(
    async (id) => {
      try {
        setSubmitting(true);
        await deleteElement(id);
        const newData = await fetchServiceConfiguration();
        if (openBlock === id) {
          setOpenBlock(null);
        }
        if (openSecondBlock === id) {
          setOpenSecondBlock(null);
        }
        setDeleteBlock(null);
        setData(newData);
        await fetchServiceVersions();
      } catch (error) {
        console.log(error);
      } finally {
        setSubmitting(false);
      }
    },
    [
      setData,
      setSubmitting,
      deleteElement,
      fetchServiceConfiguration,
      setDeleteBlock,
      openBlock,
      openSecondBlock,
      setOpenBlock,
      setOpenSecondBlock,
    ]
  );

  const createAndFetchNewElement = useCallback(
    async (data) => {
      try {
        setSubmitting(true);
        await createElement({
          ...defaultConfigurationRow,
          service_id: parseInt(serviceId, 10),
          flg_output_visibility: data.type === 'output' ? 3 : null,
          flg_deleted: false,
          version: newVersion || currentVersion,
          ...data,
        });
        const newData = await fetchServiceConfiguration();
        setData(newData);
        await fetchServiceVersions();
      } catch (error) {
        console.log(error);
      } finally {
        setSubmitting(false);
      }
    },
    [
      setData,
      setSubmitting,
      createElement,
      fetchServiceConfiguration,
      serviceId,
    ]
  );

  const createNewChecklist = async () => {
    setSubmitting(true);
    try {
      await dispatchChecklist(null, null);
    }
    catch (error) {
      push({type: 'error', title: intl.formatMessage({id: 'server_error'})});
      throw error;
    }
    finally {
      setSubmitting(false);
    }
  };

  const createNewAp = async () => {
    setSubmitting(true);
    try {
      await dispatchAp(null, null);
    }
    catch (error) {
      push({type: 'error', title: intl.formatMessage({id: 'server_error'})});
      throw error;
    }
    finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (services)
      setService(services.find((s) => s.id === parseInt(serviceId, 10)));
  }, [services])

  useEffect(() => {
    let cancel = false;

    if (!roleTypes && !serviceLocale) {
      fetchConfiguration(cancel);
    }
    return () => {
      cancel = true;
    };
    // eslint-disable-next-line
  }, [fetchServiceConfiguration]);


  // Fetch associated quotation (iff service is a design service)
  useEffect(() => {
    setTCSMetadata_(service?.metadata_?.TCS);
    if (
      !service
      || service.class !== 0  /* Progettazione */
      || associatedQuotationService !== undefined
    )
      return;
    (async () => {
      const result = await countryApiRequest.get(`/services/associations/${service.id}`);
      setAssociatedQuotationService(result?.[0] || null);
    })();
  }, [service]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch associated design services (iff service is a quotation service)
  useEffect(() => {
    if (!service || service.class !== 1  /* Quotazione */)
      // exit if service is not loaded yet or if it is a design service
      return;
    (async () => {
      const result = await countryApiRequest.get(`/services/associations/${service.id}`);
      setAssociatedDesignServices(result);
    })();
  }, [service?.associated_services, countryApiRequest, service])

  useEffect(() => {
    if (!service)
      return;
    (async () => {
      await fetchServiceVersions();
      setCurrentVersion(service.active_version);
    })();
  }, [service])

  useEffect(() => {
    if (!service || !currentVersion)
      return;
    (async () => {
      setSubmitting(true);
      const result = await fetchServiceConfiguration();
      const crossResult = await fetchCrossServiceConfiguration();
      setData(result);
      setCrossData(crossResult);
      setSubmitting(false);
    })();
  }, [service, currentVersion]);

  useEffect(() => {
    setNewVersion(null);
  }, [versions]);

  if (error) {
    return <ErrorPage />;
  }

  if (!data) {
    return <ListPlaceholder />;
  }
  return (
    <Fragment>
      {blocksSelected.length ? (
        <ModalPortal>
          <div className="fixed top-0 left-0 w-full p-4 bg-white flex justify-end border-b-2 border-black">
            <p className=" mr-4 font-bold text-xl">{`Selected elements: ${blocksSelected.length}`}</p>
            <Button
              onClick={() => {
                setBlocksSelected([]);
              }}
            >
              CLEAR
            </Button>
          </div>
        </ModalPortal>
      ) : null}
      <div>
        {submitting ? (
          <ModalPortal>
            <div className="fixed top-0 left-0 w-full h-full flex items-center justify-center bg-white z-10 opacity-60">
              <Spinner styleType="white" />
            </div>
          </ModalPortal>
        ) : null}
        {showAlert && (
          <Alert
            type="info"
            text={
              <FormattedMessage
                id="service_configurator_info"
                defaultMessage={`Per salvare la nuova versione apporta una modifica ad un blocco della sezione Steps`}
              />
            }
            className="py-6 mt-4 mb-4"
            marginTop
          />
        )}
        <PageHeading
          title={
            <div className="flex flex-row items-center gap-2">
              <span>{service.name}</span>
              <Badge
                type={labelData?.type}
                text={
                  !!labelData
                    ? <span className="font-sans font-medium uppercase">
                      <FormattedMessage id={labelData.labelId} defaultMessage={labelData.labelDefaultMessage} />
                    </span>
                    : <></>
                }
                textSize="text-xs"
              />
            </div>
          }
          historyBackPath={`/${country.code}/explorer/services`}
          contentActions={
            <div>
              {service?.class === 0 /* Progettazione */ && (
                <Button
                  className="ml-4"
                  styleType="empty"
                  onClick={() => {
                    setServiceAssociationModalOpen(true);
                  }}
                  disabled={associatedQuotationService === undefined}
                >
                  {associatedQuotationService === undefined ? (
                    <ArrowPathIcon className="w-4 mt-0.5 mr-0.5 animate-spin" />
                  ) : (
                    <div className="relative">
                      <LinkIcon className="w-4" />
                      {!!associatedQuotationService && (
                        <div
                          // Diagonal bar
                          className="absolute top-0 left-1/3 w-0.5 h-full transform -rotate-45 bg-red-500 opacity-50"
                        />
                      )}
                    </div>
                  )}
                  <FormattedMessage
                    id={
                      !!associatedQuotationService
                        ? "disassociate"
                        : "associate"
                    }
                  />
                </Button>
              )}
            </div>
          }
        />
        {/*<div className="w-full border-t border-black mb-9"/>*/}
        <div className={"mb-10"}>
          <ServiceConfiguratorVersionManager
            service={service}
            versions={versions}
            versionsWithNew={versionsWithNew}
            currentVersion={currentVersion}
            newVersion={newVersion}
            setCurrentVersion={setCurrentVersion}
            setNewVersion={setNewVersion}
            fetchServiceVersions={fetchServiceVersions}
            approveDraftVersion={approveDraftVersion}
            deleteDraftVersion={deleteDraftVersion}
            setSubmitting={setSubmitting}
          />
        </div>
        <div className="flex gap-3 mb-5">
          {/*{SECTIONS.map((s, idx) =>*/}
          {/*  <Button*/}
          {/*    key={idx}*/}
          {/*    styleType={selectedSection === s ? 'default' : 'empty'}*/}
          {/*    paddingCls="px-6 py-1.5"*/}
          {/*    onClick={() => {*/}
          {/*      setSelectedSection(s);*/}
          {/*    }}*/}
          {/*  >*/}
          {/*    <FormattedMessage id={s}/>*/}
          {/*  </Button>*/}
          {/*)}*/}
          <Tabs
            tabs={SECTIONS.map((s) => ({
              name: intl.formatMessage({ id: s }),
            }))}
            active={SECTIONS.indexOf(selectedSection)}
            setActiveTab={(i) => setSelectedSection(SECTIONS[i])}
          />
        </div>
        <div className={"flex items-center justify-between mb-5"}>
          <div>
            {service?.class === 1 /* Quotazione */ &&
              associatedDesignServices?.length > 0 && (
                <div>
                  <h3 className="text-base font-semibold">
                    <FormattedMessage id="associated_design_services" />
                  </h3>
                  <ul className="mt-2">
                    {associatedDesignServices.map((s, idx) => (
                      <li key={idx} className="flex gap-2 items-center my-1">
                        <LinkIcon className="w-3" />
                        <Link
                          to={`/${country.code}/service-configuration/${s.id}`}
                          target="_blank"
                        >
                          <span className="text-ag-500 hover:text-ag-700 hover:underline">
                            {s.name}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
          </div>
          <div className="flex items-center gap-2">
            <Link
              to={`/${country.code}/service-configuration/${serviceId}/preview`}
            >
              <Button styleType="secondary">PREVIEW</Button>
            </Link>
            <Button
              submitting={isTCSLoading}
              variant="contained"
              color="primary"
              hideSpinner
              onClick={async () => {
                if (!service?.metadata_?.TCS) {
                  // TO CHECK
                  setTCSMetadata_({
                    // Metadata like the service_configuration
                    tcsType: "kerakoll",
                    webCategoriaProdotto: "",
                    webMotivoContatto: "",
                    alertText: "",
                    emailBody: "",
                    recipientEmail: "",
                    questions: [],
                    dependencies: [],
                    active: false,
                  });
                }
                setOpenTCSBlock(!openTCSBlock);
              }}
            >
              {service?.metadata_?.TCS ? (
                <>
                  {service.metadata_.TCS.active ? <CheckCircleIcon className={`w-5 flex-shrink-0`}/> : null}
                  <UserIcon
                    className="-ml-0.5 mr-1 my-0.5 h-4 w-4"
                    aria-hidden="true"
                  />
                  <span className="mb-0.5">
                    <FormattedMessage
                      id="update_TCS"
                      defaultMessage="Aggiorna TCS"
                    />
                  </span>
                </>
              ) : (
                <>
                  <UserPlusIcon
                    className="-ml-0.5 mr-1 my-0.5 h-4 w-4"
                    aria-hidden="true"
                  />
                  <span className="mb-0.5">
                    <FormattedMessage
                      id="setup_TCS"
                      defaultMessage="Configura TCS"
                    />
                  </span>
                </>
              )}
            </Button>
            <Button
              submitting={submitting}
              variant="contained"
              color="primary"
              hideSpinner
              onClick={async () => {
                if (selectedSection === "steps")
                  await createAndFetchNewElement({
                    type: "step",
                    order: data.filter((d) => d.type === "step" || d.type === "service").length + 1,
                  });
                else if (selectedSection === "checklists")
                  await createNewChecklist();
                else if (selectedSection === "ap") await createNewAp();
                else
                  throw new Error(`unsupported section '${selectedSection}'`);
              }}
            >
              <PlusCircleIcon
                className="-ml-0.5 mr-1 my-0.5 h-4 w-4"
                aria-hidden="true"
              />
              <span className="mb-0.5">
                {selectedSection === "steps" && (
                  <FormattedMessage id="add_step" defaultMessage="Nuovo step" />
                )}
                {selectedSection === "checklists" && (
                  <FormattedMessage
                    id="add_checklist"
                    defaultMessage="Nuova checklist"
                  />
                )}
                {selectedSection === "ap" && (
                  <FormattedMessage
                    id="add_ap"
                    defaultMessage="Nuova analisi prezzo"
                  />
                )}
              </span>
            </Button>
            {
              selectedSection === 'steps' && (
                <Button
                  submitting={submitting}
                  variant="contained"
                  color="primary"
                  hideSpinner
                  onClick={async () => {
                    await createAndFetchNewElement({
                      type: "service",
                      order: data.filter((d) => d.type === "step" || d.type === "service").length + 1,
                    });
                  }}
                >
                  <PlusCircleIcon
                    className="-ml-0.5 mr-1 my-0.5 h-4 w-4"
                    aria-hidden="true"
                  />
                  <span>
                    <FormattedMessage
                      id={"add_service"}
                      defaultMessage={"Aggiungi Configuratore"}
                    />
                  </span>
                </Button>
              )
            }
          </div>
        </div>

        {selectedSection === "steps" && (
          <ServiceConfiguratorBlocksList
            setOpenBlock={setOpenBlock}
            openBlock={openBlock}
            data={data}
            setData={setData}
            roleTypes={roleTypes}
            createAndFetchNewElement={createAndFetchNewElement}
            moveBlock={moveBlock}
            selectBlock={selectBlock}
            unselectBlock={unselectBlock}
            pasteBlocks={pasteBlocks}
            selectedIds={blocksSelected.map((b) => b.id)}
            service={service}
            crossData={crossData}
            setCrossData={setCrossData}
            openBlockServiceBlock={openBlockServiceBlock}
            setOpenBlockServiceBlock={setOpenBlockServiceBlock}
          />
        )}
        {selectedSection === "checklists" && (
          <ServiceChecklistListing
            serviceId={serviceId}
            submittingChecklist={submitting}
            data={data}
          />
        )}
        {selectedSection === "ap" && (
          <ServiceApListing
            serviceId={serviceId}
            submittingAp={submitting}
            data={data}
          />
        )}

        {openBlock ? (
          <ServiceConfiguratorOpenBlock
            setDeleteBlock={setDeleteBlock}
            serviceLocale={serviceLocale}
            roleTypes={roleTypes}
            createAndFetchNewElement={createAndFetchNewElement}
            block={data.find((b) => b.id === openBlock)}
            data={data}
            setCreateTranslation={setCreateTranslation}
            openSecondBlock={openSecondBlock}
            setOpenSecondBlock={setOpenSecondBlock}
            setData={setData}
            moveBlock={moveBlock}
            deleteAndFetch={deleteAndFetch}
            setOpenBlock={setOpenBlock}
            fetchConfiguration={async () => {
              const newData = await fetchServiceConfiguration();
              setData(newData);
            }}
            service={service}
            associatedDesignServices={associatedDesignServices}
            version={newVersion || currentVersion}
            fetchServiceVersions={fetchServiceVersions}
            crossData={crossData}
            setCrossData={setCrossData}
            openBlockServiceBlock={openBlockServiceBlock}
            services={services}
            fetchCrossServicesConfigurations={async () => {
              const newCrossData = await fetchCrossServiceConfiguration();
              setCrossData(newCrossData);
            }}
          />
        ) : null}
        {openSecondBlock ? (
          <ServiceConfiguratorOpenBlock
            second="right-1/2 lg:right-1/3"
            setCreateTranslation={setCreateTranslation}
            setDeleteBlock={setDeleteBlock}
            roleTypes={roleTypes}
            createAndFetchNewElement={createAndFetchNewElement}
            block={data.find((b) => b.id === openSecondBlock)}
            data={data}
            serviceLocale={serviceLocale}
            setData={setData}
            setOpenBlock={setOpenSecondBlock}
            fetchConfiguration={async () => {
              const newData = await fetchServiceConfiguration();
              setData(newData);
            }}
            service={service}
            version={newVersion || currentVersion}
            fetchServiceVersions={fetchServiceVersions}
            crossData={crossData}
            setCrossData={setCrossData}
            openBlockServiceBlock={openBlockServiceBlock}
            services={services}
            fetchCrossServicesConfigurations={async () => {
              const newCrossData = await fetchCrossServiceConfiguration();
              setCrossData(newCrossData);
            }}
          />
        ) : null}
        {openTCSBlock ? (
          <>
            <TCSServiceModal
              service={service}
              data={data}
              setService={setService}
              isTCSLoading={isTCSLoading}
              setIsTCSLoading={setIsTCSLoading}
              TCSMetadata_={TCSMetadata_}
              setOpenTCSBlock={setOpenTCSBlock}
              setTCSMetadata_={setTCSMetadata_}
            />
          </>
        ) : null}
        {deleteBlock ? (
          <ServiceConfiguratorDeleteBlock
            deleteBlock={deleteBlock}
            setDeleteBlock={setDeleteBlock}
            data={data}
            deleteAndFetch={deleteAndFetch}
          />
        ) : null}
        {createTranslation ? (
          <ServiceConfiguratorCreateTranslation
            createTranslation={createTranslation}
            setCreateTranslation={setCreateTranslation}
            setServiceLocale={setServiceLocale}
          />
        ) : null}
        {serviceAssociationModalOpen && (
          <AssociateServicesModal
            service={service}
            association={associatedQuotationService}
            onConfirm={(s) => {
              setAssociatedQuotationService(s || null);
              setServiceAssociationModalOpen(false);
            }}
            onClose={() => {
              setServiceAssociationModalOpen(false);
            }}
          />
        )}
      </div>
      <Route
        exact
        path={`/${country.code}/service-configuration/${serviceId}/preview`}
        render={() => (
          <ModalPortal>
            <ServiceConfiguratorPreview
              returnPath={`/${country.code}/service-configuration/${serviceId}`}
              configuration={data}
              roleTypes={roleTypes}
              service={service}
              crossServiceConfigurations={crossData}
            />
          </ModalPortal>
        )}
      />
    </Fragment>
  );
};

export default ServiceConfigurator;
